import { alpha, darken } from "@mui/material/styles";
import { shapePill, heightCta } from "../theme/shape";

/**
 * Single source of truth for MagicFurnish dialog/modal button styles.
 *
 * Primary tiers (use these for any new CTA):
 *   getMFPrimaryButtonSx       → brand pill with auto-contrast label.
 *                                Universal primary CTA — login, signup,
 *                                AuthModal, gallery inquire, editor Apply,
 *                                editor Make Magic, admin dialogs. One
 *                                recipe everywhere; contrast is guaranteed
 *                                by theme.palette.primary.contrastText.
 *   getMFSecondaryButtonSx     → outlined neutral pill (Cancel, Back).
 *   getMFDestructiveButtonSx   → error pill (Delete, Remove).
 *
 * Opt-in alternate (rare):
 *   getSpatialPrimaryButtonSx  → perceived-glass pill, dark text. Use only
 *                                when a primary CTA must NOT carry tenant
 *                                color (e.g. a future co-branded surface
 *                                where two brands compete). Default callers
 *                                should prefer getMFPrimaryButtonSx so the
 *                                app stays unified.
 *
 * All existing callers of getAuthPrimaryButtonSx continue to work — that
 * function in authGlassStyles.js delegates here.
 */

export { getSpatialPrimaryButtonSx } from "../theme/spatial";

const BRAND_FALLBACK = "#006766";

function brandPrimary(theme) {
  return (
    theme?.palette?.customPrimary?.main ||
    theme?.palette?.primary?.main ||
    BRAND_FALLBACK
  );
}

/**
 * Primary CTA — pill, brand gradient, premium shadow.
 * Apply to: contained LoadingButton / Button for the main action in any dialog.
 */
export function getMFPrimaryButtonSx(theme) {
  const p = brandPrimary(theme);
  return {
    textTransform: "none !important",
    fontWeight: 600,
    fontSize: "16px",
    letterSpacing: "0em",
    minHeight: heightCta,
    borderRadius: `${shapePill} !important`,
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(180deg, ${p} 0%, ${darken(p, 0.10)} 100%)`,
    border: "none",
    boxShadow: [
      `inset 0 1px 0 ${alpha("#ffffff", 0.18)}`,
      `0 1px 3px rgba(0,0,0,0.20)`,
      `0 4px 14px ${alpha(p, 0.30)}`,
    ].join(", "),
    "&:hover": {
      background: `linear-gradient(180deg, ${darken(p, 0.04)} 0%, ${darken(p, 0.14)} 100%)`,
      boxShadow: [
        `inset 0 1px 0 ${alpha("#ffffff", 0.15)}`,
        `0 1px 4px rgba(0,0,0,0.24)`,
        `0 6px 20px ${alpha(p, 0.40)}`,
      ].join(", "),
    },
  };
}

/**
 * Secondary CTA — pill, outlined neutral.
 * Pair with variant="outlined" on the MUI button so the theme provides
 * the border colour; this function handles shape + typography only.
 * Apply to: Cancel / Back buttons.
 */
export function getMFSecondaryButtonSx() {
  return {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "16px",
    letterSpacing: "0em",
    minHeight: heightCta,
    borderRadius: shapePill,
  };
}

/**
 * Destructive CTA — pill, error-colour gradient.
 * Apply to: Delete / Remove confirmation buttons.
 */
export function getMFDestructiveButtonSx(theme) {
  const err = theme.palette.error.main;
  return {
    textTransform: "none !important",
    fontWeight: 600,
    fontSize: "16px",
    letterSpacing: "0em",
    minHeight: heightCta,
    borderRadius: `${shapePill} !important`,
    color: "#ffffff",
    background: `linear-gradient(180deg, ${err} 0%, ${darken(err, 0.08)} 100%)`,
    border: "none",
    boxShadow: [
      `inset 0 1px 0 ${alpha("#ffffff", 0.16)}`,
      `0 1px 3px rgba(0,0,0,0.18)`,
      `0 4px 14px ${alpha(err, 0.28)}`,
    ].join(", "),
    "&:hover": {
      background: `linear-gradient(180deg, ${darken(err, 0.04)} 0%, ${darken(err, 0.12)} 100%)`,
      boxShadow: [
        `inset 0 1px 0 ${alpha("#ffffff", 0.12)}`,
        `0 1px 4px rgba(0,0,0,0.22)`,
        `0 6px 20px ${alpha(err, 0.36)}`,
      ].join(", "),
    },
  };
}
